(() => {
  const moneyFallback = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

  const initCollection = (root) => {
    const tabs = [...root.querySelectorAll('[data-model-trigger]')];
    if (!tabs.length) return;

    const activate = (id) => {
      tabs.forEach((tab) => {
        const active = tab.dataset.modelTrigger === id;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-pressed', String(active));
        const focusLabel = tab.querySelector('[data-focus-label]');
        if (focusLabel) focusLabel.textContent = active ? 'Comparison focus' : 'Focus this model';
      });
      root.querySelectorAll('[data-renew-card]').forEach((card) => {
        card.classList.toggle('is-selected', card.dataset.modelId === id);
      });
      root.querySelectorAll('[data-comparison-panel]').forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.comparisonPanel === id);
      });
      const source = tabs.find((tab) => tab.dataset.modelTrigger === id && tab.dataset.modelTitle);
      const answer = root.querySelector('[data-choice-answer]');
      if (source && answer) {
        const title = answer.querySelector('[data-choice-title]');
        const copy = answer.querySelector('[data-choice-copy]');
        if (title) title.textContent = source.dataset.modelTitle || '';
        if (copy) copy.textContent = source.dataset.modelCopy || '';
      }
    };

    tabs.forEach((tab) => tab.addEventListener('click', () => activate(tab.dataset.modelTrigger)));
    const initial = tabs.find((tab) => tab.classList.contains('is-active')) || tabs[0];
    if (initial) activate(initial.dataset.modelTrigger);
  };

  const initGallery = (root) => {
    const thumbs = root.querySelector('[data-gallery-thumbs]');
    const scrollButtons = [...root.querySelectorAll('[data-gallery-scroll]')];
    const mobileQuery = window.matchMedia('(max-width: 989px)');

    const scrollState = () => {
      if (!thumbs) return { horizontal: false, position: 0, maximum: 0 };
      const horizontal = mobileQuery.matches;
      return {
        horizontal,
        position: horizontal ? thumbs.scrollLeft : thumbs.scrollTop,
        maximum: horizontal ? thumbs.scrollWidth - thumbs.clientWidth : thumbs.scrollHeight - thumbs.clientHeight
      };
    };

    const updateScrollButtons = () => {
      if (mobileQuery.matches) {
        scrollButtons.forEach((button) => {
          button.hidden = true;
          button.disabled = true;
        });
        return;
      }
      const { position, maximum } = scrollState();
      const overflow = maximum > 2;
      scrollButtons.forEach((button) => {
        button.hidden = !overflow;
        button.disabled = !overflow || (button.dataset.galleryScroll === 'previous' ? position <= 1 : position >= maximum - 1);
      });
    };

    const revealThumb = (thumb) => {
      if (!thumb || !thumbs || mobileQuery.matches) return;
      if (typeof thumb.scrollIntoView === 'function') {
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
      window.requestAnimationFrame(updateScrollButtons);
    };

    const activateMedia = (id) => {
      if (!id) return;
      root.querySelectorAll('[data-gallery-item]').forEach((item) => {
        item.classList.toggle('is-active', item.dataset.galleryItem === String(id));
      });
      let activeThumb = null;
      root.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
        const active = thumb.dataset.galleryThumb === String(id);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
        if (active) activeThumb = thumb;
      });
      revealThumb(activeThumb);
    };

    root.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => activateMedia(thumb.dataset.galleryThumb));
    });
    scrollButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (!thumbs) return;
        const { horizontal } = scrollState();
        const direction = button.dataset.galleryScroll === 'previous' ? -1 : 1;
        const distance = horizontal ? thumbs.clientWidth : thumbs.clientHeight;
        thumbs.scrollBy({
          left: horizontal ? direction * distance : 0,
          top: horizontal ? 0 : direction * distance,
          behavior: 'smooth'
        });
      });
    });
    if (thumbs) thumbs.addEventListener('scroll', updateScrollButtons, { passive: true });
    window.addEventListener('resize', updateScrollButtons);
    if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', updateScrollButtons);
    window.requestAnimationFrame(updateScrollButtons);
    return activateMedia;
  };

  const initProduct = (root) => {
    const activateMedia = initGallery(root);
    const form = root.querySelector('[data-product-form]');
    const jsonNode = root.querySelector('[data-variant-json]');
    if (!form || !jsonNode) return;

    let variants = [];
    try {
      variants = JSON.parse(jsonNode.textContent || '[]');
    } catch (error) {
      return;
    }
    if (!Array.isArray(variants) || !variants.length) return;

    let config = {};
    const configNode = root.querySelector('[data-variant-config]');
    if (configNode) {
      try {
        config = JSON.parse(configNode.textContent || '{}');
      } catch (error) {
        config = {};
      }
    }
    const useSellingPlan = config.useSellingPlan !== false;
    const planMatch = String(config.sellingPlanMatch || '').trim().toLowerCase();

    const variantInput = form.querySelector('[data-variant-id]');
    const sellingPlanInput = form.querySelector('[data-selling-plan]');
    const price = root.querySelector('[data-product-price]');
    const comparePrice = root.querySelector('[data-compare-price]');
    const stickyPrice = root.querySelector('[data-sticky-price]');
    const availability = root.querySelector('[data-product-availability]');
    const buyButtons = [...root.querySelectorAll('[data-buy-button]')];

    // Every rendered option group, paired with the option's real position in
    // variant.options. Keying off the index (not DOM order) keeps the lookup
    // correct even when a single-value group is not rendered at all.
    const groups = [...root.querySelectorAll('[data-option-group]')].map((element) => ({
      element,
      index: Number(element.dataset.optionIndex || 0),
      buttons: [...element.querySelectorAll('[data-option-value]')]
    }));

    // A third-party storefront script writes display:none !important onto the
    // non-selected button in the first option group after this asset initializes.
    // CSS cannot override an inline !important declaration, so remove only that
    // hostile property and keep watching the option buttons for it being reapplied.
    const optionButtons = groups.flatMap(({ buttons }) => buttons);
    const restoreOptionVisibility = (button) => {
      if (button.style.display !== 'none') return;
      button.style.removeProperty('display');
      if (!button.getAttribute('style')) button.removeAttribute('style');
    };
    optionButtons.forEach(restoreOptionVisibility);
    if (optionButtons.length && 'MutationObserver' in window) {
      const visibilityObserver = new MutationObserver((records) => {
        records.forEach(({ target }) => restoreOptionVisibility(target));
      });
      optionButtons.forEach((button) => {
        visibilityObserver.observe(button, { attributes: true, attributeFilter: ['style'] });
      });
    }

    let activeVariant = null;

    // Full option tuple: start from the active variant so unrendered options
    // keep their value, then layer on whatever the shopper has selected.
    const currentSelection = () => {
      const values = (activeVariant || variants[0]).options.slice();
      groups.forEach(({ element, index }) => {
        const active = element.querySelector('[data-option-value].is-active');
        if (active) values[index] = active.dataset.optionValue;
      });
      return values;
    };

    const findExact = (values) =>
      variants.find((variant) => values.every((value, index) => variant.options[index] === value));

    const planFor = (variant) => {
      if (!useSellingPlan) return null;
      const plans = Array.isArray(variant.sellingPlans) ? variant.sellingPlans : [];
      if (!plans.length) return null;
      if (planMatch) {
        const matched = plans.find((plan) => String(plan.name || '').toLowerCase().includes(planMatch));
        if (matched) return matched.id;
      }
      return null;
    };

    const syncButtons = (variant) => {
      groups.forEach(({ index, buttons }) => {
        buttons.forEach((button) => {
          restoreOptionVisibility(button);
          const active = button.dataset.optionValue === variant.options[index];
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
      });
    };

    // Dim values that cannot be bought alongside the rest of the current
    // selection. They stay clickable so the picker never dead-ends.
    const markAvailability = (variant) => {
      groups.forEach(({ index, buttons }) => {
        buttons.forEach((button) => {
          const probe = variant.options.slice();
          probe[index] = button.dataset.optionValue;
          const candidate = findExact(probe);
          button.classList.toggle('is-unavailable', !candidate || !candidate.available);
        });
      });
    };

    const applyVariant = (variant, updateUrl) => {
      if (!variant) return;
      activeVariant = variant;

      if (variantInput) variantInput.value = variant.id;

      const planId = planFor(variant);
      const planReady = !useSellingPlan || Boolean(sellingPlanInput && planId);
      if (sellingPlanInput) {
        if (planId) {
          sellingPlanInput.value = planId;
          sellingPlanInput.disabled = false;
        } else {
          sellingPlanInput.value = '';
          sellingPlanInput.disabled = true;
        }
      }

      const priceText = variant.priceText || moneyFallback(variant.price);
      if (price) price.textContent = priceText;
      if (stickyPrice) stickyPrice.textContent = priceText;
      if (comparePrice) {
        comparePrice.textContent = variant.comparePriceText || '';
        comparePrice.hidden = !variant.comparePriceText;
      }
      if (availability) {
        availability.textContent = variant.available
          ? availability.dataset.availableLabel || 'Available'
          : availability.dataset.soldOutLabel || 'Sold out';
      }
      buyButtons.forEach((button) => {
        button.disabled = !variant.available || !planReady;
        if (!variant.available) {
          button.textContent = button.dataset.soldOutLabel || 'Sold out';
        } else if (!planReady) {
          button.textContent = button.dataset.planUnavailableLabel || 'Pre-order unavailable';
        } else {
          button.textContent = button.dataset.availableLabel || 'Add to cart';
        }
      });
      if (variant.mediaId && activateMedia) activateMedia(variant.mediaId);

      syncButtons(variant);
      markAvailability(variant);

      if (updateUrl) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        } catch (error) {
          /* URL rewriting is a nicety - never block the picker on it. */
        }
      }
    };

    groups.forEach(({ index, buttons }) => {
      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const values = currentSelection();
          values[index] = button.dataset.optionValue;

          // Exact combination first. If that tuple does not exist, honour the
          // value just clicked and move to the closest variant carrying it,
          // preferring one that is in stock.
          const variant =
            findExact(values) ||
            variants.find((candidate) => candidate.options[index] === button.dataset.optionValue && candidate.available) ||
            variants.find((candidate) => candidate.options[index] === button.dataset.optionValue);

          applyVariant(variant, true);
        });
      });
    });

    const startingId = variantInput ? String(variantInput.value) : '';
    const startingVariant =
      variants.find((variant) => String(variant.id) === startingId) ||
      variants.find((variant) => variant.available) ||
      variants[0];
    applyVariant(startingVariant, false);

    const purchasePanel = root.querySelector('[data-purchase-panel]');
    const sticky = root.querySelector('[data-mobile-buy]');
    if (purchasePanel && sticky && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        ([entry]) => sticky.classList.toggle('is-visible', !entry.isIntersecting),
        { threshold: 0.08 }
      );
      observer.observe(purchasePanel);
    }
  };

  const initComparison = (root) => {
    const focusSelect = root.querySelector('[data-focus-select]');
    const rivalSelect = root.querySelector('[data-rival-select]');
    if (!rivalSelect && !focusSelect) return;
    const applyFocus = (id) => {
      root.querySelectorAll('[data-focus-cell]').forEach((cell) => {
        cell.hidden = cell.dataset.focusCell !== id;
      });
      const label = root.querySelector('[data-focus-heading]');
      const option = focusSelect && focusSelect.options[focusSelect.selectedIndex];
      if (label && option) label.textContent = option.textContent;
    };
    const applyRival = (id) => {
      root.querySelectorAll('[data-rival-cell]').forEach((cell) => {
        cell.hidden = cell.dataset.rivalCell !== id;
      });
      const label = root.querySelector('[data-rival-heading]');
      const option = rivalSelect.options[rivalSelect.selectedIndex];
      if (label && option) label.textContent = option.textContent;
    };
    if (focusSelect) {
      focusSelect.addEventListener('change', () => applyFocus(focusSelect.value));
      applyFocus(focusSelect.value);
    }
    if (rivalSelect) {
      rivalSelect.addEventListener('change', () => applyRival(rivalSelect.value));
      applyRival(rivalSelect.value);
    }
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-virro-collection]').forEach(initCollection);
    scope.querySelectorAll('[data-virro-product]').forEach(initProduct);
    scope.querySelectorAll('[data-virro-comparison]').forEach(initComparison);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
